import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Paper, 
  BottomNavigation, 
  BottomNavigationAction,
  styled,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccountCircleRounded as ProfileIcon,
  LocalMallRounded as ShopIcon,
  EventAvailableRounded as BookingIcon,
  SpaceDashboardRounded as DashboardIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import AuthDialog from './AuthDialog';

const GlassNav = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(20px)',
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
  borderTop: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
  zIndex: 1000,
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'block',
  },
}));

const StyledBottomNavigation = styled(BottomNavigation)({
  background: 'transparent',
  height: 45,
});

const StyledBottomNavigationAction = styled(BottomNavigationAction)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
  '& .MuiBottomNavigationAction-label': {
    fontSize: '0.75rem',
    transition: 'font-size 0.2s, opacity 0.2s',
    opacity: 1,  // Always show label
    '&.Mui-selected': {
      fontSize: '0.75rem',
    },
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.5rem',  // Slightly smaller icons to accommodate always-visible labels
    transition: 'transform 0.2s ease',
    marginBottom: '4px',  // Add some space between icon and label
  },
  '&.Mui-selected .MuiSvgIcon-root': {
    transform: 'scale(1.1)',
  },
  '&:hover .MuiSvgIcon-root': {
    transform: 'scale(1.1)',
  },
  padding: '6px 0 8px',
  minWidth: 'auto',  // Allow buttons to be more compact
  flex: 1,  // Ensure equal width distribution
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(20px)',
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    minWidth: 180,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  },
}));

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isVerySmallScreen = useMediaQuery('(max-width:390px)');

  const getActiveRoute = () => {
    const path = location.pathname;
    if (path === '/login') return '/profile';  // Show profile as active when on login page
    if (path.includes('/profile')) return '/profile';
    if (path.includes('/shop')) return '/shop';
    if (path.includes('/services')) return '/services';
    if (path.includes('/dashboard') || path.includes('/client') || path.includes('/barber')) {
      return '/dashboard';
    }
    return '/';
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    if (user) {
      event.preventDefault();
      event.stopPropagation();
      setProfileMenuAnchor(event.currentTarget);
    } else {
      setAuthDialogOpen(true);
    }
  };

  const handleMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleSignOut = async () => {
    handleMenuClose();
    await logout();
    navigate('/');
  };

  const handleNavigation = (route: string) => {
    switch (route) {
      case '/profile':
        if (!user) {
          setAuthDialogOpen(true);
        } else {
          navigate('/profile');
        }
        break;
      case '/dashboard':
        if (!user) {
          setAuthDialogOpen(true);
        } else {
          navigate(`/${user.type}`);
        }
        break;
      case '/shop':
        navigate('/barber-shops');
        break;
      case '/services':
        navigate('/');  // Navigate to booking flow
        break;
      default:
        navigate(route);
    }
  };

  return (
    <>
      <GlassNav elevation={0}>
        <StyledBottomNavigation
          value={getActiveRoute()}
          onChange={(_, newValue) => handleNavigation(newValue)}
          sx={{ width: '100%' }}
        >
          <StyledBottomNavigationAction
            label={user ? t('nav.profile') : t('nav.signin')}
            value="/profile"
            icon={<ProfileIcon />}
            onClick={handleProfileClick}
          />
          <StyledBottomNavigationAction
            label={t('nav.shop')}
            value="/shop"
            icon={<ShopIcon />}
          />
          <StyledBottomNavigationAction
            label={t('nav.book')}
            value="/services"
            icon={<BookingIcon />}
          />
          <StyledBottomNavigationAction
            label={t('nav.dashboard')}
            value="/dashboard"
            icon={<DashboardIcon />}
          />
        </StyledBottomNavigation>
      </GlassNav>

      <StyledMenu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <MenuItem onClick={() => {
          handleMenuClose();
          navigate('/profile');
        }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('nav.profile')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('nav.signout')}</ListItemText>
        </MenuItem>
      </StyledMenu>

      <AuthDialog 
        open={authDialogOpen} 
        onClose={() => setAuthDialogOpen(false)} 
      />
    </>
  );
};

export default BottomNav; 